import axios, { AxiosInstance, AxiosResponse } from 'axios'

import HttpServiceBase, {
  HttpRequestBase,
  HttpServiceBaseResponse,
} from '~/services/http/models'

enum EHttpMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

class AxiosHttpService implements HttpServiceBase {
  private axiosInstance: AxiosInstance

  constructor(name: string, baseURL: string) {
    if (!baseURL) {
      throw new Error(
        `Base URL for ${name} is missing. Did you forget to set the environment variable?`,
      )
    }

    this.axiosInstance = axios.create({
      baseURL,
      headers: {},
    })
  }

  public updateToken(accessToken: string | undefined) {
    this.axiosInstance.defaults.headers.Authorization = `Bearer ${accessToken}`
  }

  public getInterceptors() {
    return this.axiosInstance.interceptors
  }

  private buildUrl(path: string, query?: Record<string, string>): string {
    const url = new URL(path, this.axiosInstance.defaults.baseURL)
    if (query) {
      Object.keys(query).forEach((key) =>
        url.searchParams.append(key, query[key]),
      )
    }
    return url.toString()
  }

  private async request<T>(
    method: EHttpMethod,
    path: string,
    query?: Record<string, string>,
    payload?: unknown,
  ): Promise<HttpServiceBaseResponse<T>> {
    const url = this.buildUrl(path, query)

    const response: AxiosResponse<T> = await this.axiosInstance.request<T>({
      method,
      url,
      data: payload,
    })

    return response.data as HttpServiceBaseResponse<T>
  }

  async get<T>({
    path,
    query,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>(EHttpMethod.GET, path, query)
  }

  async post<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>(EHttpMethod.POST, path, query, payload)
  }

  async put<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>(EHttpMethod.PUT, path, query, payload)
  }

  async patch<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>(EHttpMethod.PATCH, path, query, payload)
  }

  async delete<T>({
    path,
    query,
    payload,
  }: HttpRequestBase): Promise<HttpServiceBaseResponse<T>> {
    return this.request<T>(EHttpMethod.DELETE, path, query, payload)
  }
}

export default AxiosHttpService
